import React, { useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Clock, Users, Layers, BarChart3, PieChart, Calculator, Calendar, TrendingUp, Package } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart as RePieChart, Pie, Cell } from 'recharts';

// Cores para gráficos
const CORES_GRAFICO = ['#3b82f6', '#f97316', '#22c55e', '#8b5cf6', '#ef4444', '#06b6d4', '#eab308', '#ec4899', '#6b7280', '#78350f'];

// Minutos num dia de trabalho (padrão)
const MINUTOS_DIA_TRABALHO = 460;

// Componente de Análise de Tempos de uma OF
export default function AnaliseTemposOF({ 
  open, 
  onOpenChange, 
  of, // A OF completa
  operacoesBase, // Lista de todas as operações do sistema (para obter nomes e categorias)
  categorias, // Lista de categorias
  quantidade // Quantidade da OF para cálculos
}) {
  // Estado das tabs
  const [activeTab, setActiveTab] = useState('porPeca'); // 'porPeca', 'tempoTotal', 'calculadora'
  
  // Estado da calculadora
  const [numOperadores, setNumOperadores] = useState(1);
  const [eficiencia, setEficiencia] = useState(90); // Percentagem (90 = 90%)
  const [minutosTrabalho, setMinutosTrabalho] = useState(MINUTOS_DIA_TRABALHO);
  
  // Calcular dados da análise
  const analise = useMemo(() => {
    if (!of?.operacoes || !operacoesBase || operacoesBase.length === 0) {
      return null;
    }

    const operacoesOF = of.operacoes || [];
    // Usar quantidade_total da OF (campo correto)
    const qtd = of.quantidade_total || of.quantidade || quantidade || 1;
    
    // Mapear operações da OF com dados completos
    const operacoesComDados = operacoesOF.map(opOf => {
      const opBase = operacoesBase.find(o => o.id === opOf.operacao_id);
      return {
        ...opOf,
        codigo: opBase?.codigo || 'N/A',
        nome: opBase?.nome || 'Operação desconhecida',
        categoria: opBase?.categoria || opBase?.categoria_nome || 'Sem categoria',
        tempo: opOf.tempo_ajustado || opBase?.tempo_standard || 0,
        numOperadores: (opOf.operadores_atribuidos || []).length
      };
    });

    // Tempo total da OF (por peça)
    const tempoTotalPorPeca = operacoesComDados.reduce((acc, op) => acc + op.tempo, 0);
    
    // Tempo total para todas as peças da OF (em segundos)
    const tempoTotalOF = tempoTotalPorPeca * qtd;

    // Agrupar por categoria
    const porCategoria = {};
    operacoesComDados.forEach(op => {
      const cat = op.categoria;
      if (!porCategoria[cat]) {
        porCategoria[cat] = {
          nome: cat,
          operacoes: [],
          tempoTotal: 0,
          numOperacoes: 0
        };
      }
      porCategoria[cat].operacoes.push(op);
      porCategoria[cat].tempoTotal += op.tempo;
      porCategoria[cat].numOperacoes += 1;
    });

    // Converter para array e calcular percentagens
    const categoriasAnalise = Object.values(porCategoria).map((cat, idx) => {
      const catInfo = categorias?.find(c => c.nome === cat.nome);
      return {
        ...cat,
        cor: catInfo?.cor || CORES_GRAFICO[idx % CORES_GRAFICO.length],
        codigo: catInfo?.codigo || cat.nome.substring(0, 3).toUpperCase(),
        percentagemOperacoes: ((cat.numOperacoes / operacoesComDados.length) * 100).toFixed(1),
        percentagemTempo: tempoTotalPorPeca > 0 ? ((cat.tempoTotal / tempoTotalPorPeca) * 100).toFixed(1) : 0,
        tempoTotalOF: cat.tempoTotal * qtd, // Tempo total para todas as peças
        operacoes: cat.operacoes.map(op => ({
          ...op,
          percentagemOF: tempoTotalPorPeca > 0 ? ((op.tempo / tempoTotalPorPeca) * 100).toFixed(1) : 0,
          percentagemCategoria: cat.tempoTotal > 0 ? ((op.tempo / cat.tempoTotal) * 100).toFixed(1) : 0,
          tempoTotalOF: op.tempo * qtd // Tempo total para todas as peças
        }))
      };
    });

    // Ordenar categorias pela ordem definida
    categoriasAnalise.sort((a, b) => {
      const catA = categorias?.find(c => c.nome === a.nome);
      const catB = categorias?.find(c => c.nome === b.nome);
      return (catA?.ordem || 999) - (catB?.ordem || 999);
    });

    // Dados para gráfico de pizza (distribuição de tempo por categoria)
    const dadosPizza = categoriasAnalise.map(cat => ({
      name: cat.nome,
      value: cat.tempoTotal,
      percentagem: parseFloat(cat.percentagemTempo),
      fill: cat.cor
    }));

    // Dados para gráfico de barras (operações ordenadas por tempo)
    const dadosBarras = [...operacoesComDados]
      .sort((a, b) => b.tempo - a.tempo)
      .slice(0, 10)
      .map(op => ({
        codigo: op.codigo,
        nome: op.nome.length > 25 ? op.nome.substring(0, 25) + '...' : op.nome,
        tempo: op.tempo,
        categoria: op.categoria
      }));

    return {
      totalOperacoes: operacoesComDados.length,
      totalCategorias: categoriasAnalise.length,
      tempoTotalPorPeca, // em segundos
      tempoTotalOF, // em segundos (para todas as peças)
      quantidade: qtd,
      categorias: categoriasAnalise,
      dadosPizza,
      dadosBarras,
      operacoesComDados
    };
  }, [of, operacoesBase, categorias, quantidade]);

  // Formatar tempo (segundos para string legível)
  const formatTempo = (segundos) => {
    if (!segundos || segundos === 0) return '0s';
    const horas = Math.floor(segundos / 3600);
    const minutos = Math.floor((segundos % 3600) / 60);
    const segs = Math.round(segundos % 60);
    
    if (horas > 0) {
      return minutos > 0 ? `${horas}h ${minutos}min` : `${horas}h`;
    }
    if (minutos > 0) {
      return segs > 0 ? `${minutos}min ${segs}s` : `${minutos}min`;
    }
    return `${segs}s`;
  };

  // Formatar tempo longo (para grandes quantidades - mostra dias, horas, minutos)
  const formatTempoLongo = (segundos) => {
    if (!segundos || segundos === 0) return '0';
    const totalMinutos = segundos / 60;
    const dias = Math.floor(totalMinutos / (minutosTrabalho || MINUTOS_DIA_TRABALHO));
    const horasRestantes = Math.floor((totalMinutos % (minutosTrabalho || MINUTOS_DIA_TRABALHO)) / 60);
    const minutosRestantes = Math.floor(totalMinutos % 60);
    
    if (dias > 0) {
      if (horasRestantes > 0) {
        return `${dias}d ${horasRestantes}h`;
      }
      return `${dias} dias`;
    }
    if (horasRestantes > 0) {
      return minutosRestantes > 0 ? `${horasRestantes}h ${minutosRestantes}min` : `${horasRestantes}h`;
    }
    return `${minutosRestantes}min`;
  };

  // Cálculos da calculadora de recursos
  const calculoRecursos = useMemo(() => {
    if (!analise) return null;
    
    const eficienciaDecimal = (eficiencia || 100) / 100;
    const tempoTotalMinutos = analise.tempoTotalOF / 60; // Converter segundos para minutos
    
    // Tempo real considerando eficiência (se eficiência for 90%, demora mais 10%)
    const tempoRealMinutos = tempoTotalMinutos / eficienciaDecimal;
    
    // Minutos disponíveis por dia considerando número de operadores
    const minutosDisponiveis = (minutosTrabalho || MINUTOS_DIA_TRABALHO) * (numOperadores || 1);
    
    // Dias necessários
    const diasNecessarios = tempoRealMinutos / minutosDisponiveis;
    
    // Horas totais de trabalho
    const horasTotais = tempoRealMinutos / 60;
    
    return {
      tempoTotalMinutos,
      tempoRealMinutos,
      diasNecessarios: Math.ceil(diasNecessarios * 10) / 10, // Arredondar para 1 casa decimal
      diasNecessariosExato: diasNecessarios,
      horasTotais: Math.ceil(horasTotais * 10) / 10,
      minutosDisponiveis
    };
  }, [analise, numOperadores, eficiencia, minutosTrabalho]);

  if (!analise) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Análise de Tempos</DialogTitle>
          </DialogHeader>
          <div className="text-center py-8 text-stone-500">
            <Clock className="h-12 w-12 mx-auto mb-4 text-stone-300" />
            <p>Sem operações para analisar</p>
            <p className="text-sm mt-2">Adicione operações à OF para ver a análise</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const qtd = analise.quantidade;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-blue-600" />
            Análise de Tempos - {of.numero_of || of.ref || 'OF'}
          </DialogTitle>
          <p className="text-sm text-stone-500">
            {of.cliente || of.marca} • {of.artigo} • {qtd} peças
          </p>
        </DialogHeader>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-stone-200 mb-4">
          <button
            onClick={() => setActiveTab('porPeca')}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
              activeTab === 'porPeca' 
                ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-600' 
                : 'text-stone-600 hover:bg-stone-50'
            }`}
          >
            <Clock className="h-4 w-4 inline mr-2" />
            Por Peça
          </button>
          <button
            onClick={() => setActiveTab('tempoTotal')}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
              activeTab === 'tempoTotal' 
                ? 'bg-green-50 text-green-700 border-b-2 border-green-600' 
                : 'text-stone-600 hover:bg-stone-50'
            }`}
          >
            <Package className="h-4 w-4 inline mr-2" />
            Tempo Total ({qtd} pç)
          </button>
          <button
            onClick={() => setActiveTab('calculadora')}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
              activeTab === 'calculadora' 
                ? 'bg-purple-50 text-purple-700 border-b-2 border-purple-600' 
                : 'text-stone-600 hover:bg-stone-50'
            }`}
          >
            <Calculator className="h-4 w-4 inline mr-2" />
            Calculadora
          </button>
        </div>

        {/* Tab: Por Peça */}
        {activeTab === 'porPeca' && (
          <div className="space-y-6">
            {/* Cards de Resumo */}
            <div className="grid grid-cols-4 gap-4">
              <Card className="p-4 text-center bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                <Layers className="h-6 w-6 mx-auto mb-2 text-blue-600" />
                <p className="text-2xl font-bold text-blue-700">{analise.totalOperacoes}</p>
                <p className="text-xs text-blue-600">Operações</p>
              </Card>
              <Card className="p-4 text-center bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                <PieChart className="h-6 w-6 mx-auto mb-2 text-purple-600" />
                <p className="text-2xl font-bold text-purple-700">{analise.totalCategorias}</p>
                <p className="text-xs text-purple-600">Categorias</p>
              </Card>
              <Card className="p-4 text-center bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
                <Clock className="h-6 w-6 mx-auto mb-2 text-amber-600" />
                <p className="text-2xl font-bold text-amber-700">{formatTempo(analise.tempoTotalPorPeca)}</p>
                <p className="text-xs text-amber-600">Tempo/Peça</p>
              </Card>
              <Card className="p-4 text-center bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                <Users className="h-6 w-6 mx-auto mb-2 text-green-600" />
                <p className="text-2xl font-bold text-green-700">{formatTempoLongo(analise.tempoTotalOF)}</p>
                <p className="text-xs text-green-600">Total ({qtd} pç)</p>
              </Card>
            </div>

            {/* Gráficos lado a lado */}
            <div className="grid grid-cols-2 gap-6">
              {/* Gráfico de Pizza */}
              <Card className="p-4">
                <h3 className="font-semibold text-stone-800 mb-4 flex items-center gap-2">
                  <PieChart className="h-4 w-4 text-stone-500" />
                  Distribuição de Tempo por Categoria
                </h3>
                <ResponsiveContainer width="100%" height={220}>
                  <RePieChart>
                    <Pie
                      data={analise.dadosPizza}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, percentagem }) => `${name} ${percentagem}%`}
                      labelLine={true}
                    >
                      {analise.dadosPizza.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value) => [`${formatTempo(value)} (${((value / analise.tempoTotalPorPeca) * 100).toFixed(1)}%)`, 'Tempo/Peça']}
                    />
                  </RePieChart>
                </ResponsiveContainer>
              </Card>

              {/* Gráfico de Barras */}
              <Card className="p-4">
                <h3 className="font-semibold text-stone-800 mb-4 flex items-center gap-2">
                  <BarChart3 className="h-4 w-4 text-stone-500" />
                  Top 10 Operações (por tempo)
                </h3>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={analise.dadosBarras} layout="vertical" margin={{ left: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" tickFormatter={(v) => `${v}s`} />
                    <YAxis type="category" dataKey="codigo" width={55} tick={{ fontSize: 11 }} />
                    <Tooltip 
                      formatter={(value) => [`${formatTempo(value)}`, 'Tempo/Peça']}
                      labelFormatter={(label) => analise.dadosBarras.find(o => o.codigo === label)?.nome || label}
                    />
                    <Bar dataKey="tempo" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            </div>

            {/* Tabela Detalhada por Categoria */}
            <Card className="p-4">
              <h3 className="font-semibold text-stone-800 mb-4">
                Detalhe por Categoria
                <span className="text-xs text-stone-400 font-normal ml-2">
                  (para distribuição de mão de obra)
                </span>
              </h3>
              <div className="space-y-4 max-h-80 overflow-y-auto">
                {analise.categorias.map((cat) => (
                  <div key={cat.nome} className="border rounded-lg overflow-hidden">
                    <div 
                      className="flex items-center justify-between p-3"
                      style={{ backgroundColor: `${cat.cor}15`, borderLeft: `4px solid ${cat.cor}` }}
                    >
                      <div className="flex items-center gap-3">
                        <span 
                          className="font-mono text-sm font-bold px-2 py-0.5 rounded" 
                          style={{ backgroundColor: cat.cor, color: 'white' }}
                        >
                          {cat.codigo}
                        </span>
                        <span className="font-semibold text-stone-800">{cat.nome}</span>
                        <span className="text-xs px-2 py-0.5 bg-white/50 rounded-full text-stone-600">
                          {cat.numOperacoes} op ({cat.percentagemOperacoes}%)
                        </span>
                      </div>
                      <div className="flex items-center gap-6 text-sm">
                        <div className="text-center">
                          <span className="font-bold" style={{ color: cat.cor }}>{cat.percentagemTempo}%</span>
                          <p className="text-[10px] text-stone-500">do tempo</p>
                        </div>
                        <div className="text-center">
                          <span className="font-semibold text-stone-700">{formatTempo(cat.tempoTotal)}</span>
                          <p className="text-[10px] text-stone-500">por peça</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-stone-50/50 p-2">
                      <div className="grid grid-cols-12 gap-2 text-[10px] text-stone-500 font-medium mb-1 px-2">
                        <span className="col-span-2">Código</span>
                        <span className="col-span-4">Operação</span>
                        <span className="col-span-2 text-center">% OF</span>
                        <span className="col-span-2 text-center">% Cat.</span>
                        <span className="col-span-1 text-center">Ops</span>
                        <span className="col-span-1 text-right">Tempo</span>
                      </div>
                      <div className="space-y-1">
                        {cat.operacoes
                          .sort((a, b) => b.tempo - a.tempo)
                          .map((op) => (
                          <div key={op.operacao_id} className="grid grid-cols-12 gap-2 text-sm py-1.5 px-2 bg-white rounded border border-stone-100 hover:bg-blue-50/50 transition-colors">
                            <span className="col-span-2 font-mono font-medium text-primary text-xs">{op.codigo}</span>
                            <span className="col-span-4 text-stone-700 truncate text-xs" title={op.nome}>{op.nome}</span>
                            <div className="col-span-2 flex items-center gap-1">
                              <div className="flex-1 h-2 bg-stone-200 rounded-full overflow-hidden">
                                <div 
                                  className="h-full rounded-full bg-blue-500"
                                  style={{ width: `${Math.min(100, parseFloat(op.percentagemOF))}%` }}
                                />
                              </div>
                              <span className="text-[10px] text-stone-500 w-8 text-right">{op.percentagemOF}%</span>
                            </div>
                            <div className="col-span-2 flex items-center gap-1">
                              <div className="flex-1 h-2 bg-stone-200 rounded-full overflow-hidden">
                                <div 
                                  className="h-full rounded-full"
                                  style={{ width: `${Math.min(100, parseFloat(op.percentagemCategoria))}%`, backgroundColor: cat.cor }}
                                />
                              </div>
                              <span className="text-[10px] text-stone-500 w-8 text-right">{op.percentagemCategoria}%</span>
                            </div>
                            <span className="col-span-1 text-center">
                              {op.numOperadores > 0 ? (
                                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-green-100 text-green-700 text-[10px] font-medium">
                                  {op.numOperadores}
                                </span>
                              ) : (
                                <span className="text-stone-300">-</span>
                              )}
                            </span>
                            <span className="col-span-1 text-right font-medium text-stone-800 text-xs">{formatTempo(op.tempo)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}

        {/* Tab: Tempo Total */}
        {activeTab === 'tempoTotal' && (
          <div className="space-y-6">
            {/* Cards de Resumo - Tempo Total */}
            <div className="grid grid-cols-4 gap-4">
              <Card className="p-4 text-center bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                <Package className="h-6 w-6 mx-auto mb-2 text-blue-600" />
                <p className="text-2xl font-bold text-blue-700">{qtd}</p>
                <p className="text-xs text-blue-600">Total Peças</p>
              </Card>
              <Card className="p-4 text-center bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                <Layers className="h-6 w-6 mx-auto mb-2 text-purple-600" />
                <p className="text-2xl font-bold text-purple-700">{analise.totalOperacoes}</p>
                <p className="text-xs text-purple-600">Operações</p>
              </Card>
              <Card className="p-4 text-center bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
                <Clock className="h-6 w-6 mx-auto mb-2 text-amber-600" />
                <p className="text-2xl font-bold text-amber-700">{formatTempoLongo(analise.tempoTotalOF)}</p>
                <p className="text-xs text-amber-600">Tempo Total</p>
              </Card>
              <Card className="p-4 text-center bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                <Calendar className="h-6 w-6 mx-auto mb-2 text-green-600" />
                <p className="text-2xl font-bold text-green-700">
                  {Math.ceil(analise.tempoTotalOF / 60 / MINUTOS_DIA_TRABALHO * 10) / 10}
                </p>
                <p className="text-xs text-green-600">Dias (1 pessoa)</p>
              </Card>
            </div>

            {/* Tabela de Tempo Total por Categoria */}
            <Card className="p-4">
              <h3 className="font-semibold text-stone-800 mb-4 flex items-center gap-2">
                <Clock className="h-4 w-4 text-stone-500" />
                Tempo Total por Categoria ({qtd} peças)
              </h3>
              <div className="space-y-3">
                {analise.categorias.map((cat) => (
                  <div key={cat.nome} className="border rounded-lg overflow-hidden">
                    <div 
                      className="flex items-center justify-between p-4"
                      style={{ backgroundColor: `${cat.cor}15`, borderLeft: `4px solid ${cat.cor}` }}
                    >
                      <div className="flex items-center gap-3">
                        <span 
                          className="font-mono text-sm font-bold px-2 py-0.5 rounded" 
                          style={{ backgroundColor: cat.cor, color: 'white' }}
                        >
                          {cat.codigo}
                        </span>
                        <span className="font-semibold text-stone-800">{cat.nome}</span>
                      </div>
                      <div className="flex items-center gap-8">
                        <div className="text-center">
                          <span className="text-lg font-bold" style={{ color: cat.cor }}>{cat.percentagemTempo}%</span>
                          <p className="text-xs text-stone-500">do total</p>
                        </div>
                        <div className="text-center">
                          <span className="text-lg font-semibold text-stone-700">{formatTempo(cat.tempoTotal)}</span>
                          <p className="text-xs text-stone-500">por peça</p>
                        </div>
                        <div className="text-center min-w-[100px]">
                          <span className="text-lg font-bold text-blue-600">{formatTempoLongo(cat.tempoTotalOF)}</span>
                          <p className="text-xs text-stone-500">{qtd} peças</p>
                        </div>
                        <div className="text-center">
                          <span className="text-lg font-semibold text-green-600">
                            {Math.ceil(cat.tempoTotalOF / 60 / MINUTOS_DIA_TRABALHO * 10) / 10}d
                          </span>
                          <p className="text-xs text-stone-500">dias</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Operações com tempo total */}
                    <div className="bg-stone-50/50 p-2">
                      <div className="grid grid-cols-10 gap-2 text-[10px] text-stone-500 font-medium mb-1 px-2">
                        <span className="col-span-2">Código</span>
                        <span className="col-span-3">Operação</span>
                        <span className="col-span-1 text-center">% Cat.</span>
                        <span className="col-span-2 text-center">Tempo/Peça</span>
                        <span className="col-span-2 text-right">Tempo Total</span>
                      </div>
                      <div className="space-y-1">
                        {cat.operacoes
                          .sort((a, b) => b.tempo - a.tempo)
                          .map((op) => (
                          <div key={op.operacao_id} className="grid grid-cols-10 gap-2 text-sm py-2 px-2 bg-white rounded border border-stone-100">
                            <span className="col-span-2 font-mono font-medium text-primary text-xs">{op.codigo}</span>
                            <span className="col-span-3 text-stone-700 truncate text-xs" title={op.nome}>{op.nome}</span>
                            <span className="col-span-1 text-center text-xs text-stone-600">{op.percentagemCategoria}%</span>
                            <span className="col-span-2 text-center font-medium text-stone-800 text-xs">{formatTempo(op.tempo)}</span>
                            <span className="col-span-2 text-right font-bold text-blue-600 text-xs">{formatTempoLongo(op.tempoTotalOF)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            {/* Resumo Final */}
            <Card className="p-4 bg-gradient-to-r from-blue-50 to-green-50 border-blue-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-stone-800">Resumo Total da OF</h3>
                  <p className="text-sm text-stone-500">{qtd} peças × {formatTempo(analise.tempoTotalPorPeca)} por peça</p>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-bold text-blue-700">{formatTempoLongo(analise.tempoTotalOF)}</p>
                  <p className="text-sm text-stone-500">
                    ≈ {Math.ceil(analise.tempoTotalOF / 60 / MINUTOS_DIA_TRABALHO * 10) / 10} dias (1 pessoa, {MINUTOS_DIA_TRABALHO}min/dia)
                  </p>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Tab: Calculadora de Recursos */}
        {activeTab === 'calculadora' && (
          <div className="space-y-6">
            {/* Inputs da Calculadora */}
            <Card className="p-6 bg-gradient-to-br from-purple-50 to-blue-50 border-purple-200">
              <h3 className="font-semibold text-stone-800 mb-4 flex items-center gap-2">
                <Calculator className="h-5 w-5 text-purple-600" />
                Calculadora de Recursos
              </h3>
              
              <div className="grid grid-cols-3 gap-6">
                {/* Número de Operadores */}
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    <Users className="h-4 w-4 inline mr-1" />
                    Número de Operadores
                  </label>
                  <Input
                    type="number"
                    value={numOperadores}
                    onChange={(e) => setNumOperadores(Math.max(1, parseInt(e.target.value) || 1))}
                    min="1"
                    className="text-lg font-semibold"
                  />
                  <p className="text-xs text-stone-500 mt-1">Quantos operadores podem trabalhar em paralelo</p>
                </div>
                
                {/* Eficiência */}
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    <TrendingUp className="h-4 w-4 inline mr-1" />
                    Eficiência (%)
                  </label>
                  <Input
                    type="number"
                    value={eficiencia}
                    onChange={(e) => setEficiencia(Math.max(1, Math.min(100, parseInt(e.target.value) || 100)))}
                    min="1"
                    max="100"
                    className="text-lg font-semibold"
                  />
                  <p className="text-xs text-stone-500 mt-1">Ex: 90 = 90% de eficiência (padrão: 100%)</p>
                </div>
                
                {/* Minutos por Dia */}
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    <Clock className="h-4 w-4 inline mr-1" />
                    Minutos/Dia de Trabalho
                  </label>
                  <Input
                    type="number"
                    value={minutosTrabalho}
                    onChange={(e) => setMinutosTrabalho(Math.max(60, parseInt(e.target.value) || MINUTOS_DIA_TRABALHO))}
                    min="60"
                    className="text-lg font-semibold"
                  />
                  <p className="text-xs text-stone-500 mt-1">Padrão: 460 min (≈ 7h40)</p>
                </div>
              </div>
            </Card>

            {/* Resultado da Calculadora */}
            {calculoRecursos && (
              <>
                <div className="grid grid-cols-4 gap-4">
                  <Card className="p-4 text-center bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                    <Calendar className="h-8 w-8 mx-auto mb-2 text-purple-600" />
                    <p className="text-4xl font-bold text-purple-700">{calculoRecursos.diasNecessarios}</p>
                    <p className="text-sm text-purple-600">Dias Necessários</p>
                  </Card>
                  <Card className="p-4 text-center bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                    <Clock className="h-8 w-8 mx-auto mb-2 text-blue-600" />
                    <p className="text-4xl font-bold text-blue-700">{calculoRecursos.horasTotais}</p>
                    <p className="text-sm text-blue-600">Horas Totais</p>
                  </Card>
                  <Card className="p-4 text-center bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                    <Users className="h-8 w-8 mx-auto mb-2 text-green-600" />
                    <p className="text-4xl font-bold text-green-700">{numOperadores}</p>
                    <p className="text-sm text-green-600">Operadores</p>
                  </Card>
                  <Card className="p-4 text-center bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
                    <TrendingUp className="h-8 w-8 mx-auto mb-2 text-amber-600" />
                    <p className="text-4xl font-bold text-amber-700">{eficiencia}%</p>
                    <p className="text-sm text-amber-600">Eficiência</p>
                  </Card>
                </div>

                {/* Detalhes do Cálculo */}
                <Card className="p-4">
                  <h3 className="font-semibold text-stone-800 mb-4">Detalhes do Cálculo</h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between py-2 border-b border-stone-100">
                      <span className="text-stone-600">Quantidade de peças:</span>
                      <span className="font-semibold">{qtd} peças</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-stone-100">
                      <span className="text-stone-600">Tempo por peça:</span>
                      <span className="font-semibold">{formatTempo(analise.tempoTotalPorPeca)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-stone-100">
                      <span className="text-stone-600">Tempo total (100% eficiência):</span>
                      <span className="font-semibold">{Math.round(calculoRecursos.tempoTotalMinutos)} min ({formatTempoLongo(analise.tempoTotalOF)})</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-stone-100">
                      <span className="text-stone-600">Tempo real (com {eficiencia}% eficiência):</span>
                      <span className="font-semibold text-amber-600">{Math.round(calculoRecursos.tempoRealMinutos)} min</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-stone-100">
                      <span className="text-stone-600">Minutos disponíveis/dia ({numOperadores} operador{numOperadores > 1 ? 'es' : ''} × {minutosTrabalho} min):</span>
                      <span className="font-semibold text-green-600">{calculoRecursos.minutosDisponiveis} min/dia</span>
                    </div>
                    <div className="flex justify-between py-3 bg-purple-50 rounded-lg px-3 mt-4">
                      <span className="text-purple-800 font-medium">Dias necessários para concluir:</span>
                      <span className="font-bold text-purple-700 text-lg">{calculoRecursos.diasNecessarios} dias</span>
                    </div>
                  </div>
                </Card>

                {/* Fórmula */}
                <div className="text-xs text-stone-500 text-center p-3 bg-stone-50 rounded-lg">
                  <strong>Fórmula:</strong> Dias = (Tempo Total ÷ Eficiência) ÷ (Operadores × Minutos/Dia)
                  <br />
                  ({Math.round(calculoRecursos.tempoTotalMinutos)} ÷ {eficiencia/100}) ÷ ({numOperadores} × {minutosTrabalho}) = {calculoRecursos.diasNecessariosExato.toFixed(2)} dias
                </div>
              </>
            )}
          </div>
        )}

        {/* Nota informativa */}
        <div className="text-xs text-stone-500 text-center p-2 bg-blue-50 rounded-lg border border-blue-200 mt-4">
          <strong>Dica:</strong> Use esta análise para distribuir operadores pelas tarefas com maior carga de trabalho.
        </div>
      </DialogContent>
    </Dialog>
  );
}
